import React, { useEffect, useState } from "react";
import { api, fetchAuthedBlobUrl, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { FileText, Upload, Trash2, Download } from "lucide-react";
import { toast } from "sonner";

const MAX_BYTES = 8 * 1024 * 1024;

// Trainers upload one CBT Test PDF. Only learners who have paid them can
// download it — the server enforces that, this is just the upload surface.
export default function TrainingMaterialPanel() {
  const [materials, setMaterials] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/trainer/materials").then(r => setMaterials(r.data)).catch(() => setMaterials([]));
  useEffect(() => { load(); }, []);

  const current = materials[0];

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_BYTES) {
      toast.error(`File too large — max 8 MB. This file is ${(f.size / 1024 / 1024).toFixed(1)} MB.`);
      e.target.value = "";
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post("/trainer/materials", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("CBT Test uploaded");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    }
    setBusy(false);
    e.target.value = "";
  };

  const download = async () => {
    try {
      const url = await fetchAuthedBlobUrl(`/materials/${current.material_id}/download`);
      const a = document.createElement("a");
      a.href = url;
      a.download = current.original_filename || "cbt-test.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not download");
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove this CBT Test? Learners will lose access to it.")) return;
    try {
      await api.delete(`/trainer/materials/${current.material_id}`);
      toast.success("Material removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not remove");
    }
  };

  return (
    <div className="border border-border bg-card p-6 space-y-4" data-testid="training-material-panel">
      <div>
        <div className="font-display text-xl font-medium">Training Materials</div>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Upload your <strong>CBT Test</strong> as a PDF (max 8 MB). Only learners who have paid for a
          lesson or package with you can download it.
        </p>
      </div>

      {current ? (
        <div className="flex flex-wrap items-center gap-3 border border-border p-4" data-testid="material-current">
          <FileText className="w-5 h-5 text-primary shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{current.original_filename || "CBT Test.pdf"}</div>
            <div className="text-xs text-muted-foreground">
              {current.title} · {(current.size / 1024 / 1024).toFixed(2)} MB · uploaded {new Date(current.created_at).toLocaleDateString()}
            </div>
          </div>
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" className="rounded-sm" onClick={download} data-testid="material-download-own">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Preview
            </Button>
            <Button size="sm" variant="ghost" className="rounded-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors" onClick={remove} data-testid="material-delete">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground" data-testid="material-empty">No CBT Test uploaded yet.</p>
      )}

      <label className="block max-w-xs">
        <input type="file" accept="application/pdf" className="hidden" onChange={upload} disabled={busy} data-testid="material-file-input" />
        <Button type="button" variant="outline" className="rounded-sm w-full" asChild disabled={busy}>
          <span><Upload className="w-3.5 h-3.5 mr-1.5" /> {current ? "Replace PDF" : "Upload PDF"}{busy ? "…" : ""}</span>
        </Button>
      </label>
    </div>
  );
}
